import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "./actions";
import { showAlert } from "../../config/utils";
import Portal from "../common/components/Portal";
import { DynamicSoftCostModal } from "./components/DynamicSoftCostModal";
import ConfirmationModal from "../common/components/ConfirmationModal";
import { useLocation } from "react-router-dom";

const useSoftCostDynamicField = ({ id, props }) => {
    const dispatch = useDispatch();
    const { saveDynamicSoftCostField, updateDynamicSoftCostField, deleteDynamicSoftCostField } = useSelector(state => state.softCostReducer);
    const [dynamicSoftCosts, setDynamicSoftCosts] = useState([]);
    const [showDynamicAddModal, setDynamicAddModal] = useState(false);
    const [submittingDynamicSc, setSubmittingDynamicSc] = useState(false);
    const [showDeleteConfirmation, setDeleteConfirmation] = useState(false);
    const [selectedDynamicField, setSelectedDynamicField] = useState({});
    const [refreshToggle, setRefreshToggle] = useState(false);

    const prevSaveDynamicSoftCostField = useRef(saveDynamicSoftCostField);
    const prevUpdateDynamicSoftCostField = useRef(updateDynamicSoftCostField);
    const prevDeleteDynamicSoftCostField = useRef(deleteDynamicSoftCostField);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const projectId = queryParams.get("pid") || id;
    
    useEffect(() => {
        if (saveDynamicSoftCostField && prevSaveDynamicSoftCostField.current !== saveDynamicSoftCostField) {
            const { success, message = "", error = "" } = saveDynamicSoftCostField;
            prevSaveDynamicSoftCostField.current = saveDynamicSoftCostField;
            if (success === true) {
                setRefreshToggle(!refreshToggle);
                showAlert(message || "Field Created Successfully");
            } else if (success === false) {
                showAlert(message || error || "Oops... Failed to create dynamic field!");
            }
            setSubmittingDynamicSc(false);
            setDynamicAddModal(false);
            setSelectedDynamicField(null);
        }
    }, [saveDynamicSoftCostField]);

    useEffect(() => {
        if (updateDynamicSoftCostField && prevUpdateDynamicSoftCostField.current !== updateDynamicSoftCostField) {
            const { success, message = "", error = "" } = updateDynamicSoftCostField;
            prevUpdateDynamicSoftCostField.current = updateDynamicSoftCostField;
            if (success === true) {
                setRefreshToggle(!refreshToggle);
                showAlert(message || "Field Updated Successfully");
            } else if (success === false) {
                showAlert(message || error || "Oops... Failed to Update Dynamic Field!");
            }
            setSubmittingDynamicSc(false);
            setDynamicAddModal(false);
            setSelectedDynamicField(null);
        }
    }, [updateDynamicSoftCostField]);

    useEffect(() => {
        if (deleteDynamicSoftCostField && prevDeleteDynamicSoftCostField.current !== deleteDynamicSoftCostField) {
            const { success, message = "", error = "" } = deleteDynamicSoftCostField;
            prevDeleteDynamicSoftCostField.current = deleteDynamicSoftCostField;
            if (success === true) {
                setDynamicSoftCosts(prevSf => prevSf.filter(item => item.id !== selectedDynamicField.id));
                showAlert(message || `${props.activeTab === "lease-income" ? "Income" : " Expense"} Deleted Successfully`);
            } else if (success === false) {
                showAlert(message || error || "Oops... Failed to Delete Dynamic Field!");
            }
            setDeleteConfirmation(false);
            setSelectedDynamicField(null);
        }
    }, [deleteDynamicSoftCostField]);

    const handleAddDynamicSoftCost = data => {
        const { name } = data;
        const key = name?.toLowerCase()?.split(" ").join("_");
        if (
            dynamicSoftCosts
                .filter(elem => elem.key !== selectedDynamicField?.key)
                .map(elem => elem.name)
                ?.includes(name)
        ) {
            showAlert("Field already exists!");
        } else {
            const sc = { key, ...data };
            handleSaveDynamicField(sc);
        }
    };

    const onDynamicFieldAdd = field => {
        setSelectedDynamicField(null);
        setDynamicAddModal(true);
    };

    const onDynamicFieldEdit = field => {
        setSelectedDynamicField(field);
        setDynamicAddModal(true);
    };

    const onDynamicFieldDelete = field => {
        setDeleteConfirmation(true);
        setSelectedDynamicField(field);
    };

    const handleSaveDynamicField = async sc => {
        setSubmittingDynamicSc(true);
        let data = {};
        if (selectedDynamicField?.key) {
            data = {
                id: selectedDynamicField?.id,
                label: sc.name,
                color_code: sc.color_code,
                field_type: sc.field_type,
                field_category: props.activeTab === "lease-income" ? "income" : "expense",
                last_updator_id: localStorage.getItem("userId")
            };
            dispatch(actions.updateDynamicSoftCostField(data));
        } else {
            data = {
                project_id: projectId,
                label: sc.name,
                key: sc.key,
                index: dynamicSoftCosts?.length + 1,
                actual_key: sc.key,
                field_category: props.activeTab === "lease-income" ? "income" : "expense",
                field_type: sc.field_type,
                color_code: sc.color_code,
                last_updator_id: localStorage.getItem("userId")
            };
            dispatch(actions.saveDynamicSoftCostField(data));
        }
        setSubmittingDynamicSc(false);
        setDynamicAddModal(false);
        setSelectedDynamicField(null);
    };

    const handleDeleteDynamicField = async sc => {
        if (selectedDynamicField?.id) {
            const params = {};
            dispatch(actions.deleteDynamicSoftCostField(selectedDynamicField.id, params));
        }
    };

    const renderDynamicSoftCostModal = () => {
        if (!showDynamicAddModal) return null;
        return (
            <Portal
                body={
                    <DynamicSoftCostModal
                        selectedDynamicField={selectedDynamicField}
                        onCancel={() => setDynamicAddModal(false)}
                        onFilterSave={data => handleAddDynamicSoftCost(data)}
                        isSubmitting={submittingDynamicSc}
                        heading={props.activeTab === "lease-income" ? "Income" : " Expense"}
                    />
                }
                onCancel={() => setDynamicAddModal(false)}
            />
        );
    };

    const renderDeleteConfirmationModal = () => {
        if (!showDeleteConfirmation) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={`Do you want to delete this ${props.activeTab === "lease-income" ? "Income" : " Expense"}?`}
                        message={"This action cannot be reverted, are you sure that you need to delete?"}
                        onNo={() => setDeleteConfirmation(false)}
                        onYes={() => {
                            handleDeleteDynamicField();
                        }}
                    />
                }
                onCancel={() => setDeleteConfirmation(false)}
            />
        );
    };
    return {
        dynamicSoftCosts,
        setDynamicSoftCosts,
        onDynamicFieldAdd,
        onDynamicFieldEdit,
        onDynamicFieldDelete,
        renderDynamicSoftCostModal,
        renderDeleteConfirmationModal,
        refreshToggle
    };
};

export default useSoftCostDynamicField;
